/**
 * WoList Component
 *
 * This component displays the Work Order (WO) list for a specific project.
 * It supports both active/in-progress and completed work orders, enabling users to:
 * - View, create, edit, and delete work orders
 * - Download WO documents (PDF/DOCX)
 * - Receive and view invoices for each WO
 *
 * Core Functionalities:
 * - Fetch WOs for a given project (filtered by subcontractor vendors)
 * - Tabbed display for "Active" and "Inactive" WOs (based on server-side status filtering)
 * - Integration with PayInvoice, ViewWo, and CreateWO components via routing
 * - Uses DownloadDialog and DeleteDialog modals for related actions
 *
 * Refactor Summary:
 * - Converted from Redux connect/mapDispatchToProps to functional component using hooks.
 * - Used useCallback for stable event handlers and API wrappers.
 * - Eliminated boilerplate `bindActionCreators` and used dispatch directly.
 * - Used aliases for imported functions to prevent naming conflicts (e.g., `getWOListAction`).
 * - Added Pagination and Search handlers (pageHandler, limitChangeHandler, searchHandler) to fix 'no-undef' errors.
 *
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Route, Routes, useLocation, useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import idx from "idx";
import { format } from "date-fns";
import { resolveProjectType } from "../utils/resolve-project-type";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Popover from 'react-bootstrap/Popover';
import { toUSD, numberFormat } from "../../../util/currency-formatter";
import { toDecimal, roundValue } from "../../../util/decimal-utils";
import { PerPageCount } from "../../../util/enums";
// Renamed actions to prevent naming conflicts inside the component function
import {
  getPOList as getWOListAction,
  deletePurchaseOrder as deleteWOAction,
  downloadProjectPO as downloadWOAction,
  getWorkOrderTabCounts as getWOCountsAction,
  cancelWorkOrder as cancelWOActionAction,
  reactivateWorkOrder as reactivateWOAction,
} from "../../../actions/project-management.action";
import { getGlobalSettingTemplateTypes as getTemplateTypesAction } from "../../../actions/global-setting-template.action";

// Local component/utility imports
import ViewWo from "./view-wo";
import CreateWO from "./create-work-order";
import PayInvoice from "../../vendor-payment/pay-invoice";
import DownloadDialog from "../../../components/download-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import Modal from "../../../components/modal/modal";
import CustomTable from "../../../components/table/table";
import { getProjectData } from "../utils/get-project";
import {
  VENDOR_PAYMENT,
  PROJECT_MANAGEMENT,
  CONTRACTORS,
  EDIT_WORK_ORDER,
} from "../../../constants/route-paths";


function WoList({ wContractorPermission, userAccessControl }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();

  // Project state and permissions
  const projectDetail = useSelector(state => state.projectDetail) || {};
  const projectState = useSelector(state => state.project);
  const projectType = resolveProjectType(searchParams.get("project-type")
    || idx(projectDetail, _ => _.project.projectDataType)
    || idx(projectDetail, _ => _.project.res.projectType), projectState);
  const writePermission = userAccessControl === 2 ? true : false;

  // List and Pagination state
  const [completed, setCompleted] = useState([]);
  const [pending, setPending] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [tabCounts, setTabCounts] = useState({ active: 0, inactive: 0 });
  const [limit, setLimit] = useState(PerPageCount.DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Action/Modal state
  const [selectedWoId, setSelectedWoId] = useState(null); // woId (u_id)
  const [poData, setPoData] = useState({});
  const [showPayInvoice, setShowInvoice] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [downloadData, setDownloadData] = useState({ open: false, id: "" });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);
  const [isSubContractor, setIsSubContractor] = useState(false); // Should always be true for WO list

  // --- API Callbacks ---

  const getWOList = useCallback(async () => {
    setIsLoading(true);
    try {
      // Dispatch action using the aliased name
      const { list: woList } = await dispatch(getWOListAction({
        projectId: projectId,
        vendorType: "sub-contractor",
        tab: activeTab, // Use current tab state for server-side filtering
        page: currentPage,
        limit,
        search
      }));

      const formattedList = (woList || []).map((res) => {
        const vendorName = idx(res, _ => _.vendor.business_name);
        const projectName = idx(res, _ => _.project.name);

        const poObj = {
          ...res,
          id: res.id,
          f_id: res.f_id,
          vendorName,
          projectName,
          total: parseFloat(res.woTotal || res.totalCost || 0),
          amountClaimed: parseFloat(res.amountClaimed || 0),
          retention: parseFloat(res.retention || 0),
          approved: parseFloat(res.approved || 0),
          type: idx(res, _ => _.vendor.type),
          status: res.status,
          invoicestatus: res.invoicestatus,
          issueDate: res.createdAt ? format(new Date(res.createdAt), "dd/MM/yyyy") : "",
          poId: res.u_id,
          u_id: res.u_id,
          projectId: res.projectId,
          vendor: res.vendor,
          project: res.project,
        };

        return poObj;
      });

      // Filter locally based on the statuses returned by the backend (mapped via mapStatus)
      const activeStatuses = ["Approved", "Active", "Pending"];
      const inactiveStatuses = ["Finished", "Inactive", "Completed", "Cancelled"];

      const pendingList = formattedList.filter(item => activeStatuses.includes(item.status));
      const completedList = formattedList.filter(item => inactiveStatuses.includes(item.status));

      setPending(pendingList);   // Data for Active tab
      setCompleted(completedList); // Data for Inactive tab

    } catch (e) {
      console.error('Failed to fetch WO list:', e);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, projectId, activeTab, currentPage, limit, search]);


  const getTabCounts = useCallback(async () => {
    try {
      // Dispatch action using the aliased name
      const counts = await dispatch(getWOCountsAction({ projectId, search }));
      const safeCounts = {
        active: counts?.active ?? 0,
        inactive: counts?.inactive ?? 0
      };
      setTabCounts(safeCounts);
    } catch (error) {
      console.error('Error loading WO tab counts:', error);
      setTabCounts({ active: 0, inactive: 0 });
    }
  }, [dispatch, projectId, search]);


  const gettemplate = useCallback(async (type) => {
    try {
      let newdata = [];
      const templateData = await dispatch(getTemplateTypesAction(type, null));
      if (Array.isArray(templateData)) {
        templateData.forEach((item) => {
          newdata.push({
            value: item.id,
            key: item.name,
          });
        });
      }
      setTemplateOptions(newdata);
    } catch (e) {
      console.error('Failed to fetch templates:', e);
    }
  }, [dispatch]);

  const deleteHandler = useCallback(async () => {
    deleteDialogState();
    if (selectedWoId) {
      // Dispatch action using the aliased name
      await dispatch(deleteWOAction(selectedWoId, "Work Order Deleted successfully"));
      // Re-fetch data and counts after delete
      getWOList();
      getTabCounts();
    }
  }, [selectedWoId, dispatch, getWOList, getTabCounts]);

  const handleDownloadDialog = useCallback((id = "", templateType = "work_order", overrideData = null) => {
    const currentId = id && (typeof id === 'string' || typeof id === 'number') ? id : selectedWoId;
    // When opened from wo-list menu directly, it's an event object, so check if it's a string/number
    const actualId = (typeof id === 'object') ? selectedWoId : currentId;
    const actualType = (typeof id === 'object') ? "work_order" : templateType;

    setDownloadData(prev => {
      const isOpening = !prev.open;
      const newDownloadData = { ...prev, open: isOpening, id: actualId || "", overrideData };
      if (isOpening) {
        gettemplate(actualType);
      }
      return newDownloadData;
    });
  }, [selectedWoId, gettemplate]);



  const downloadFile = useCallback(async (fileFormat = "pdf", tempId) => {
    try {
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadWOAction(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)).catch(e => console.error(e));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadWOAction(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)).catch(e => console.error(e));
      }
      handleDownloadDialog();
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [downloadData.id, downloadData.overrideData, dispatch, handleDownloadDialog]);


  // --- Use Effects for Data Loading ---

  // Effect for initial load and dependencies that trigger both list and count reload (tab, search)
  useEffect(() => {
    getTabCounts();
    getWOList();

    // Load project details if not already loaded or if the project ID changed
    if (!idx(projectDetail, _ => _.project.res) || (idx(projectDetail, _ => _.project.projectId) !== projectId)) {
      getProjectData({ projectId, dispatch });
    }
  }, [projectId, activeTab, search, location, dispatch, getWOList, getTabCounts, projectDetail]);

  // Effect for pagination and limit changes (only reloads list)
  useEffect(() => {
    if (limit === PerPageCount.DEFAULT && currentPage === 1) {
      return;
    }
    getWOList();
  }, [currentPage, limit, getWOList]);

  // --- UI/Modal Handlers ---

  const handlePayInvoiceModal = useCallback((state) => {
    setShowInvoice(prev => !prev);
    // If closing the modal (status becomes false), reload the data/counts
    if (showPayInvoice && state) {
      getWOList();
      getTabCounts();
    }
  }, [showPayInvoice, getWOList, getTabCounts]);

  const handleMenu = useCallback((rowData) => {
    setSelectedWoId(rowData.u_id);
    setPoData(rowData);
    setIsSubContractor(true);
  }, []);

  const reactivateDialogState = useCallback((id = null) => {
    if (id) setSelectedWoId(id);
    setOpenReactivateDialog(prev => !prev);
  }, []);

  const confirmReactivateWO = useCallback(async () => {
    if (selectedWoId) {
      // Dispatch action using the aliased name
      const res = await dispatch(reactivateWOAction(selectedWoId));
      if (res?.status === 'SUCCESS') {
        reactivateDialogState();
        getWOList();
        getTabCounts();
      }
    }
  }, [selectedWoId, dispatch, getWOList, getTabCounts, reactivateDialogState]);

  const cancelDialogState = useCallback((id = null) => {
    if (id) setSelectedWoId(id);
    setOpenCancelDialog(prev => !prev);
  }, []);

  const confirmCancelWO = useCallback(async () => {
    if (selectedWoId) {
      // Dispatch action using the aliased name
      const res = await dispatch(cancelWOActionAction(selectedWoId));
      if (res?.status === 'SUCCESS') {
        cancelDialogState();
        getWOList();
        getTabCounts();
      }
    }
  }, [selectedWoId, dispatch, getWOList, getTabCounts, cancelDialogState]);

  const deleteDialogState = useCallback(() => {
    setOpenDeleteDialog(prev => !prev);
  }, []);



  // --- Navigation Handlers ---
  const viewProject = useCallback(() => navigate(`/${PROJECT_MANAGEMENT}/view/${projectId}`), [navigate, projectId]);
  const viewProjectList = useCallback(() => navigate(`/${PROJECT_MANAGEMENT}?project-type=${projectType}`), [navigate, projectType]);
  const viewWO = useCallback(() => navigate(`view/${selectedWoId}`), [navigate, selectedWoId]);
  const createWO = useCallback(() => navigate(`/${PROJECT_MANAGEMENT}/work-order/${projectId}/create-work-order`, {
    state: {
      projectName: idx(projectDetail, _ => _.project.projectName),
      projectType: idx(projectDetail, _ => _.project.projectDataType)
    }
  }), [navigate, projectId, projectDetail]);
  const viewInvoices = useCallback(() => navigate(`/${VENDOR_PAYMENT}?project=${projectId}&woId=${selectedWoId}`), [navigate, projectId, selectedWoId]);

  const editWO = useCallback(() => {
    navigate(`/${CONTRACTORS}/${EDIT_WORK_ORDER.replace(':poId', selectedWoId).replace(':workType', 'project')}`, {
      state: {
        from: 'project-wo-list',
        projectId: projectId,
        projectName: idx(projectDetail, _ => _.project.projectName),
        projectType: idx(projectDetail, _ => _.project.projectDataType)
      }
    });
  }, [navigate, selectedWoId, projectId, projectDetail]);

  // --- Pagination and Search Handlers (FIXED no-undef errors) ---

  const pageHandler = useCallback((page) => {
    const newPage = page > 0 ? page : 1;
    setCurrentPage(newPage);
  }, []);

  const limitChangeHandler = useCallback((newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  }, []);

  const searchHandler = useCallback((value) => {
    if (value !== search) {
      setSearch(value);
      setCurrentPage(1);
    }
  }, [search]);

  // --- Table Configuration ---
  const columns = useMemo(() => ([
    {
      title: "WO Number",
      field: "id",
      render: (rowData) => (
        <Link to={`view/${rowData.u_id}`}>
          {rowData.f_id || rowData.id}
        </Link>
      ),
    },
    { title: "Vendor", field: "vendorName" },
    { title: "Project", field: "projectName" },
    {
      title: "WO Total",
      field: "total",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.total)}`,
    },
    {
      title: "Claimed",
      field: "amountClaimed",
      class: 'text-right',
      render: (rowData) => {
        const amountClaimed = toDecimal(rowData.amountClaimed);
        const total = amountClaimed;
        return `${numberFormat(roundValue(total, 2))}`;
      },
    },
    {
      title: "Approved",
      field: "approved",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.approved)}`,
    },
    {
      title: "Retention",
      field: "retention",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.retention)}`,
    },
    { title: "Issue Date", field: "issueDate" },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        const statusClass = rowData.status ? rowData.status.toLowerCase().replace(/ /g, '-') : '';
        return (
          <span className={`status-badge status-${statusClass}`}>
            {rowData.status}
          </span>
        );
      },
    },
  ]), []);

  const renderMenu = useCallback((item) => (
    <Popover id="popover-basic" data-cy="wo-list-table-menu">
      <Popover.Body>
        <ul className="action-block-list">
          <li onClick={viewWO} data-cy="view-wo">View WO</li>
          <li onClick={() => handleDownloadDialog(selectedWoId, "work_order")} data-cy="download-wo">Download WO</li>
          {wContractorPermission && <li onClick={() => handlePayInvoiceModal(true)} data-cy="receive-invoice">
            Receive Invoice
          </li>}
          <li onClick={viewInvoices}>View Invoices</li>
          {writePermission && (item.status === "Approved" || item.status === "Active" || item.status === "Pending") && (
            item.invoicestatus === "Not Invoiced" ? (
              <li onClick={() => cancelDialogState(item.u_id)} data-cy="cancel-wo">Cancel</li>
            ) : (item.invoicestatus === "Partially Invoiced" || item.invoicestatus === "Fully Invoiced") ? (
              <li className="disabled" data-cy="cancel-wo-disabled">
                <span title="Cannot cancel a partially or fully invoiced Work Order">Cancel</span>
              </li>
            ) : <li onClick={() => cancelDialogState(item.u_id)} data-cy="cancel-wo">Cancel</li>
          )}
          {writePermission && (item.status !== "Finished" && item.status !== "Completed") && (
            <li onClick={editWO} data-cy="edit-wo">Edit WO</li>
          )}
          {writePermission && (item.status === "Pending" || (item.status === "Approved" && item.invoicestatus === "Not Invoiced")) && (
            <li onClick={deleteDialogState} data-cy="delete-wo">Delete</li>
          )}
          {writePermission && (item.status === "Cancelled") && (
            <li onClick={() => reactivateDialogState(item.u_id)} data-cy="reactivate-wo">Reactivate</li>
          )}
        </ul>
      </Popover.Body>
    </Popover>
  ), [viewWO, handleDownloadDialog, wContractorPermission, handlePayInvoiceModal, viewInvoices, writePermission, editWO, deleteDialogState, cancelDialogState, reactivateDialogState]);

  // The active list data is determined by the tab state
  const activeListData = activeTab === 'active' ? pending : completed;

  const refreshWOList = () => {
    getWOList();
    getTabCounts();
  }

  return (
    <>
      <Routes>
        <Route
          path={`view/:woId`}
          element={<ViewWo poData={poData} wContractorPermission={wContractorPermission} userAccessControl={userAccessControl} />}
        />
        <Route
          path={`create-work-order`}
          element={<CreateWO />}
        />
        <Route
          path=""
          element={
            <>
              {showPayInvoice && (
                <PayInvoice
                  projectId={projectId}
                  vendorId={idx(poData, _ => _.vendor.id)}
                  poId={selectedWoId}
                  poNo={poData.f_id || poData.id}
                  projectName={poData.projectName}
                  vendorName={poData.vendorName}
                  isSubContractor={isSubContractor}
                  workType="project"
                  handleDownloadDialog={handleDownloadDialog}
                  handleClose={handlePayInvoiceModal}
                  getWOList={refreshWOList}
                  dataCy="receive-invoice-wo-modal"
                />
              )}
              <div className="purchase-order-block" data-cy="wo-list">
                <div className="row">
                  <div className="w-auto">
                    <h2 className="content-heading text-uppercase" data-cy="wo-list-heading">Work Order List</h2>
                    <div className="col-12 breadcrumb-block p-0" data-cy="breadcrumb">
                      <Breadcrumb>
                        <Breadcrumb.Item onClick={viewProjectList} data-cy="breadcrumb-projects">PROJECTS</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={viewProject} data-cy="breadcrumb-project">{idx(projectDetail, _ => _.project.projectName)}</Breadcrumb.Item>
                        <Breadcrumb.Item active data-cy="breadcrumb-work-order">Work Order List</Breadcrumb.Item>
                      </Breadcrumb>
                    </div>
                  </div>
                  <div className="w-auto ms-auto mt-auto">
                    {wContractorPermission && <button
                      type="submit"
                      className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary"
                      onClick={createWO}
                      data-cy="create-wo">
                      Create Work Order
                    </button>}
                  </div>
                </div>

                <div className="row">
                  <div className="col-12" data-cy="wo-list-table-container">
                    <Tabs
                      activeKey={activeTab} // Use activeKey instead of defaultActiveKey
                      id="uncontrolled-tab-example"
                      className="mb-3"
                      onSelect={setActiveTab}
                      tabClassName="wo-list-tabs"
                    >
                      <Tab eventKey="active" title={`Active (${tabCounts.active})`} tabClassName="active-tab">
                        <CustomTable
                          columns={columns}
                          data={pending} // Data for active tab should be pending list
                          isAction={true}
                          handleMenu={handleMenu}
                          dataCy="wo-list-active"
                          paginationCount={tabCounts.active}
                          currentPage={currentPage}
                          pageHandler={pageHandler}
                          limitChangeHandler={limitChangeHandler}
                          itemsPerPage={limit}
                          searchHandler={searchHandler}
                          isLoading={isLoading}
                          externalSearchValue={search}
                          externalSearchHandler={searchHandler}
                          menu={renderMenu}
                          fileName='Active WO List'
                        />
                      </Tab>
                      <Tab eventKey="inactive" title={`Inactive (${tabCounts.inactive})`} tabClassName="inactive-tab">
                        <CustomTable
                          columns={columns}
                          data={completed} // Data for inactive tab should be completed list
                          isAction={true}
                          handleMenu={handleMenu}
                          dataCy="wo-list-inactive"
                          paginationCount={tabCounts.inactive}
                          currentPage={currentPage}
                          pageHandler={pageHandler}
                          limitChangeHandler={limitChangeHandler}
                          itemsPerPage={limit}
                          searchHandler={searchHandler}
                          isLoading={isLoading}
                          externalSearchValue={search}
                          externalSearchHandler={searchHandler}
                          menu={renderMenu}
                          fileName='Inactive WO List'
                        />
                      </Tab>
                    </Tabs>

                  </div>
                </div>

              </div>
            </>
          }
        />
      </Routes>
      <DeleteDialog
        open={openDeleteDialog}
        id={selectedWoId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName="Work Order"
        description={`Work Order - ${poData.id}`}
        dataCy="delete-wo-dialog"
      />
      <Modal
        open={openCancelDialog}
        onClose={cancelDialogState}
        fullWidth={true}
        maxWidth='sm'
        modalHeading="Cancel Work Order"
        modalSaveTxt={<span data-cy="confirm-cancel">Yes</span>}
        modalCloseTxt='No'
        onClick={confirmCancelWO}
        showActionBtn
        savebtnClass='createBtn'
        cxlbtnClass='cancelBtn'
        primary
        dataCy="cancel-wo-dialog"
      >
        <p>Are you sure you want to cancel Work Order - {poData.f_id || poData.id}? </p>
      </Modal>
      <Modal
        open={openReactivateDialog}
        onClose={reactivateDialogState}
        fullWidth={true}
        maxWidth='sm'
        modalHeading="Reactivate Work Order"
        modalSaveTxt={<span data-cy="confirm-reactivate">Yes</span>}
        modalCloseTxt='No'
        onClick={confirmReactivateWO}
        showActionBtn
        savebtnClass='createBtn'
        cxlbtnClass='cancelBtn'
        primary
        dataCy="reactivate-wo-dialog"
      >
        <p>Are you sure you want to reactivate Work Order - {poData.f_id || poData.id}? </p>
      </Modal>
      <DownloadDialog
        {...downloadData}
        downloadFile={downloadFile}
        templateOptions={templateOptions}
        handleDownloadDialog={handleDownloadDialog}
        downloadModuleName="WorkOrder List"
        dataCy="download-wo-dialog"
      />
    </>
  );
}

// NOTE: Since the file provided the component was being used with react-redux connect(),
// I'm assuming that the utility functions from '../../../util/with-router' and the 
// old Redux structure are no longer needed for a clean functional component.
// The component is exported as is, relying on the parent to handle routing if necessary.

export default WoList;